import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { format, isWithinInterval, isEqual, isValid, isAfter, isBefore, parse, parseISO } from 'date-fns';
import { getScheduleDate, getInspectorOptions } from '@/store/selectors/schedule';

import PropTypes from 'prop-types';
import * as yup from 'yup';

import API from '@/apiRequest';
import { setDateTimeFromTimeString } from '@/components/SchedulePage/helpers/setDateTimeFromTimeString';
import OrderFormComponent from '@/components/SchedulePage/OrderModal/OrderForm/OrderFormComponent';
import { useUser } from '@/contexts/UserContext';
import {
  addOrUpdateOrder,
  clearFilteredInspectorsNotifications,
  deleteFilteredPendingOrder,
  setSelectedOrder,
  setUnsaved,
} from '@/store/ducks/schedule';
import { showToast } from '@/store/ducks/toasts';

const OrderFormContainer = ({ order, handleEmitOrderEditingHasBeenFinished, ...props }) => {
  const [warningInspectorMessage, setWarningInspectorMessage] = useState('');
  const [warningAvailabilityMessage, setWarningAvailabilityMessage] = useState('');
  const [scheduleDateAlert, setScheduleDateAlert] = useState(false);

  const dispatch = useDispatch();
  const inspectorOptions = useSelector(getInspectorOptions);
  const orders = useSelector(state => state.schedule.orders);
  const scheduleCurrentDateAsDateObject = useSelector(getScheduleDate);

  const intl = useIntl();
  const me = useUser();

  const handleValidateInspectorAvailability = useCallback(
    formValues => {
      const orderStartTime = formValues?.schedule?.when?.start_time;
      const orderEndTime = formValues?.schedule?.when?.end_time;

      const inspector = inspectorOptions?.find(
        option =>
          option?.value === order?.schedule?.who?.inspector_id ||
          option?.value === formValues?.schedule?.who?.inspector_id
      );

      if (
        !inspector ||
        !orderStartTime ||
        !orderEndTime ||
        orderStartTime.length < 5 ||
        orderEndTime.length < 5 ||
        orderStartTime.includes('_') ||
        orderEndTime.includes('_')
      ) {
        return;
      }

      if (Array.isArray(inspector.todayUnavailabilities)) {
        const orderStartDate = setDateTimeFromTimeString(scheduleCurrentDateAsDateObject, orderStartTime);
        const orderEndDate = setDateTimeFromTimeString(scheduleCurrentDateAsDateObject, orderEndTime);

        const isUnavailable = inspector.todayUnavailabilities.some(unavailability => {
          const interval = {
            start: typeof unavailability.start === 'string' ? parseISO(unavailability.start) : unavailability.start,
            end: typeof unavailability.end === 'string' ? parseISO(unavailability.end) : unavailability.end,
          };

          return isWithinInterval(orderStartDate, interval) || isWithinInterval(orderEndDate, interval);
        });

        if (isUnavailable) {
          setWarningAvailabilityMessage(intl.formatMessage({ id: 'UNAVAILABLE_INSPECTOR_ERROR_MESSAGE' }));

          return;
        }
      }

      if (Array.isArray(inspector.todayAvailabilities)) {
        const orderStartDate = new Date(`1970-01-01 ${orderStartTime}`);
        const orderEndDate = new Date(`1970-01-01 ${orderEndTime}`);

        const isAvailable = inspector.todayAvailabilities.some(availability => {
          const interval = {
            start: typeof availability.start_time === 'string' ? parseISO(availability.start_time) : availability.start_time,
            end: typeof availability.end_time === 'string' ? parseISO(availability.end_time) : availability.end_time,
          };

          return isWithinInterval(orderStartDate, interval) && isWithinInterval(orderEndDate, interval);
        });

        if (!isAvailable) {
          setWarningAvailabilityMessage(intl.formatMessage({ id: 'UNAVAILABLE_INSPECTOR_ERROR_MESSAGE' }));

          return;
        }
      }

      setWarningAvailabilityMessage('');
    },
    [inspectorOptions, intl, order, scheduleCurrentDateAsDateObject]
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        schedule: yup.object().shape({
          who: yup.object().shape({
            inspector_id: yup.string().required(intl.formatMessage({ id: 'REQUIRED_FIELD' })),
          }),
          flextime: yup.boolean(),
          when: yup.object().when('flextime', {
            is: true,
            then: (schema) => schema.shape({
              start_time: yup
                .string()
                .test('is-valid', intl.formatMessage({ id: 'WRONG_TIME_FORMAT' }), function checkTimeFormat(value) {
                  const date = parse(value, 'HH:mm', new Date());
                  return isValid(date);
                })
                .test('is-between', intl.formatMessage({ id: 'TIME_OUTSIDE_RANGE' }), function checkTimeRange(value) {
                  const startTime = parse('04:59', 'HH:mm', new Date());
                  const endTime = parse('20:00', 'HH:mm', new Date());
                  const date = parse(value, 'HH:mm', new Date());

                  return isWithinInterval(date, { start: startTime, end: endTime });
                })
                .required(intl.formatMessage({ id: 'REQUIRED_FIELD' })),
              end_time: yup.string().notRequired(),
            }),
            otherwise: (schema) => schema.shape({
              start_time: yup
                .string()
                .test('is-valid', intl.formatMessage({ id: 'WRONG_TIME_FORMAT' }), function checkTimeFormat(value) {
                  const date = parse(value, 'HH:mm', new Date());
                  return isValid(date);
                })
                .test('is-between', intl.formatMessage({ id: 'TIME_OUTSIDE_RANGE' }), function checkTimeRange(value) {
                  const startTime = parse('04:59', 'HH:mm', new Date());
                  const endTime = parse('20:00', 'HH:mm', new Date());
                  const date = parse(value, 'HH:mm', new Date());

                  return isWithinInterval(date, { start: startTime, end: endTime });
                })
                .required(intl.formatMessage({ id: 'REQUIRED_FIELD' })),
              end_time: yup
                .string()
                .required(intl.formatMessage({ id: 'REQUIRED_FIELD' }))
                .test('is-valid', intl.formatMessage({ id: 'WRONG_TIME_FORMAT' }), function checkTimeFormat(value) {
                  const date = parse(value, 'HH:mm', new Date());
                  return isValid(date);
                })
                .test(
                  'is-greater',
                  intl.formatMessage({ id: 'MUST_BE_LONG_THAN_START_TIME' }),
                  function validateTime(value) {
                    const { start_time: startTime } = this.parent;
                    return isAfter(parse(value, 'HH:mm', new Date()), parse(startTime, 'HH:mm', new Date()));
                  }
                )
                .test('is-between', intl.formatMessage({ id: 'TIME_OUTSIDE_RANGE' }), function checkTimeRange(value) {
                  const startTime = parse('04:59', 'HH:mm', new Date());
                  const endTime = parse('20:00', 'HH:mm', new Date());
                  const date = parse(value, 'HH:mm', new Date());

                  return isWithinInterval(date, { start: startTime, end: endTime });
                }),
            }),
          }),
        }),
      }),
    [intl]
  );

  const handleSubmit = async values => {
    let conflict = false;

    dispatch(clearFilteredInspectorsNotifications());

    orders.some(currentOrder => {
      const { date: date1, start_time: startTime1, end_time: endTime1 } = currentOrder?.schedule?.when || {};
      const { date: date2, start_time: startTime2, end_time: endTime2 } = values?.schedule?.when || {};

      const parseDateTime = (date, time) => {
        const baseDate =
          typeof date === 'string' && date.includes('-') ? parseISO(date) : parse(date, 'dd/MM/yyyy', new Date());

        return parse(time, 'HH:mm', baseDate);
      };

      const start1 = parseDateTime(date1, startTime1);
      const end1 = parseDateTime(date1, endTime1);

      const start2 = parseDateTime(date2, startTime2);
      const end2 = parseDateTime(date2, endTime2);

      // overlap check
      const overlaps = isBefore(start1, end2) && isAfter(end1, start2);

      if (
        overlaps &&
        currentOrder?.schedule?.who?.inspector_id === values?.schedule?.who?.inspector_id &&
        currentOrder?.order?.public_id !== values?.order?.public_id
      ) {
        conflict = true;
        return true;
      }

      return false;
    });

    if (conflict) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'CONFLICT' }),
          text: intl.formatMessage({ id: 'ORDER_AT_SAME_TIME_INFORMED_ERROR' }),
          duration: 5000,
        })
      );
      return;
    }

    const payload = { ...values };
    payload.schedule.when.date = format(parse(payload.schedule.when.date, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');

    if (payload.schedule.flextime) {
      let [hh, mm] = payload.schedule.when.start_time.split(':');
      hh = +hh + 1 >= 23 ? 23 : +hh + 1;
      mm = +hh + 1 >= 23 ? '59' : mm;
      payload.schedule.when.end_time = `${String(hh).padStart(2, '0')}:${mm}`;
    }

    dispatch(deleteFilteredPendingOrder(payload?.order?.public_id));
    dispatch(setSelectedOrder(null));
    dispatch(addOrUpdateOrder(payload));
    dispatch(setUnsaved(true));
    dispatch(
      showToast({
        type: 'success',
        title: intl.formatMessage({ id: 'SUCCESS' }),
        text: intl.formatMessage({ id: 'ORDER_ASSIGNED_TO_SELECTED_INSPECTOR_SUCCESS' }),
        duration: 5000,
      })
    );

    handleEmitOrderEditingHasBeenFinished(payload?.order?.public_id);

    try {
      await API.post('/scheduler/sync', {
        channel: `panel_${me.panelId}`,
        event: 'SCHEDULE.ORDER_ASSIGNED',
        data: {
          order: payload?.order,
          schedule: payload?.schedule,
          sender: {
            id: me?.uniqueId,
            who: me?.name,
            when: format(new Date(), 'yyyy-MM-dd HH:mm'),
          },
        },
      });
    } catch {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'UNABLE_SAVE_CALENDAR_DATA_ERROR_MESSAGE' }),
          duration: 3000,
        })
      );
    }
  };

  const handleSetInspectorWarningMessage = useCallback(
    (inspectorItem = {}) => {
      if (inspectorItem?.status === 'DEVICE_NOT_CONNECTED') {
        setWarningInspectorMessage(intl.formatMessage({ id: 'INSPECTOR_WITHOUT_TABLET_ERROR_MESSAGE' }));

        return;
      }

      if (inspectorItem?.pendingOrderBasedMessage) {
        if (inspectorItem?.pendingOrderBasedMessage === 'ORDER_TYPE') {
          setWarningInspectorMessage(intl.formatMessage({ id: 'DOES_NOT_DO_THE_TYPE_OF_INSPECTION_ERROR_MESSAGE' }));

          return;
        }

        if (inspectorItem?.pendingOrderBasedMessage === 'ADDRESS') {
          setWarningInspectorMessage(intl.formatMessage({ id: 'DOES_NOT_SERVER_THE_NEIGHBORHOOD_ERROR_MESSAGE' }));

          return;
        }
      }

      setWarningInspectorMessage('');
    },
    [intl]
  );

  const handleInspectorSelectChange = useCallback(
    (selectedOption, triggeredAction, form, field) => {
      if (triggeredAction.action === 'clear') {
        setWarningInspectorMessage('');
      } else {
        handleSetInspectorWarningMessage(selectedOption);
      }

      return form.setFieldValue(field?.name, selectedOption?.value);
    },
    [handleSetInspectorWarningMessage]
  );

  const handlePreSelectInspector = useCallback(
    field => {
      const preSelectedInspector = inspectorOptions?.find(option => option?.value === field?.value || '');

      if (preSelectedInspector) {
        handleSetInspectorWarningMessage(preSelectedInspector);
      }

      return preSelectedInspector;
    },
    [inspectorOptions, handleSetInspectorWarningMessage]
  );

  function parseDateFromString(dateString, fallbackTime) {
    // Accepts formats: DD/MM/YYYY, DD/MM/YYYY HH, DD/MM/YYYY HH:mm
    const [datePart, timePart] = dateString.split(' ');
    const [day, month, year] = datePart.split('/').map(Number);

    let hour = 0,
      minute = 0;
    if (timePart) {
      const timeSplit = timePart.replace(/_/g, '0').split(':');
      hour = parseInt(timeSplit[0], 10) || 0;
      minute = parseInt(timeSplit[1], 10) || 0;
    } else if (fallbackTime) {
      const [fallbackHour, fallbackMinute] = fallbackTime.replace(/_/g, '0').split(':').map(Number);
      hour = fallbackHour || 0;
      minute = fallbackMinute || 0;
    }

    return new Date(year, month - 1, day, hour, minute);
  }

  const handleValidateAccompaniedInspection = async when => {
    if (
      !order?.order?.accompanied_inspection?.scheduleDate ||
      !when?.date ||
      !when?.start_time ||
      when?.start_time.replace(/\D/g, '').length < 2
    ) {
      setScheduleDateAlert(false);
      return;
    }

    const scheduleDate = parseDateFromString(order?.order?.accompanied_inspection?.scheduleDate, when?.start_time);
    const date = parseDateFromString(`${when.date} ${when?.start_time}`);

    if (isValid(scheduleDate) && isValid(date) && !isEqual(scheduleDate, date)) {
      setScheduleDateAlert(true);
    } else {
      setScheduleDateAlert(false);
    }
  };

  return (
    <OrderFormComponent
      date={order?.schedule?.when?.date}
      validationSchema={validationSchema}
      inspectors={inspectorOptions}
      onSubmit={handleSubmit}
      warningInspectorMessage={warningInspectorMessage}
      handleInspectorSelectChange={handleInspectorSelectChange}
      handlePreSelectInspector={handlePreSelectInspector}
      intl={intl}
      handleValidateInspectorAvailability={handleValidateInspectorAvailability}
      handleValidateAccompaniedInspection={handleValidateAccompaniedInspection}
      warningAvailabilityMessage={warningAvailabilityMessage}
      scheduleDate={order?.order?.accompanied_inspection?.scheduleDate}
      scheduleDateAlert={scheduleDateAlert}
      {...props}
    />
  );
};

OrderFormContainer.propTypes = {
  order: PropTypes.object.isRequired,
  handleEmitOrderEditingHasBeenFinished: PropTypes.func.isRequired,
};

export default OrderFormContainer;
